import {
    ReactNode, useCallback, useMemo,
} from 'react';
import { PicklistValue } from '@rainbow-modules/listview/lib/components/AuditLogs/types';
import { useReduxForm } from '@rainbow-modules/hooks';
import { Option, RenderIf } from 'react-rainbow-components';
import Picklist from 'components/Picklist';

interface VoicePipelinePickerProps {
    id?: string;
    className?: string;
    name?: string;
    label?: string;
    tabIndex?: number | string;
    header?: string;
    hideHeader?: boolean;
    placeholder?: string;
    error?: ReactNode;
    required?: boolean;
    disabled?: boolean;
    readOnly?: boolean;
    value?: string;
    onChange?: (value: string) => void;
}

const pipelines = [
    { name: 'elevenlabs', label: 'ElevenLabs', value: 'elevenlabs' },
    { name: 'voxityV1', label: 'Voxity V1', value: 'voxityV1' },
    { name: 'workflow', label: 'Workflow', value: 'workflow' },
];

const VoicePipelinePicker = (props: VoicePipelinePickerProps) => {
    const {
        value: valueInProps,
        onChange = () => {},
        id,
        className,
        name,
        label,
        tabIndex,
        header = 'Select voice pipeline',
        hideHeader = false,
        placeholder,
        error,
        required,
        disabled,
        readOnly,
    } = useReduxForm(props);

    const pickListvalue = useMemo(() => {
        if (valueInProps) {
            const found = pipelines.find((pipeline) => pipeline.value === valueInProps);
            if (found) return found;
        }
        if (pipelines.length === 1) {
            const firstInList = pipelines[0];
            onChange(firstInList.value);
            return firstInList;
        }
        return undefined;
    }, [onChange, valueInProps]);

    const handleOnChange = useCallback(
        (value: PicklistValue) => onChange(value.value),
        [onChange],
    );

    const pickListOptions = useMemo(
        () => pipelines
            .map((option) => <Option key={`voice-pipeline-picker-option__${option.name}`} {...option} />),
        [],
    );

    return (
        <Picklist
            id={id}
            name={name}
            className={className}
            label={label}
            labelAlignment="left"
            tabIndex={tabIndex}
            required={required}
            placeholder={placeholder}
            value={pickListvalue}
            disabled={disabled}
            readOnly={readOnly}
            onChange={handleOnChange}
            error={error}
            borderRadius="semi-rounded"
        >
            <RenderIf isTrue={!hideHeader}>
                <Option label={header} variant="header" />
            </RenderIf>
            {pickListOptions}
        </Picklist>
    );
};

export default VoicePipelinePicker;
